import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import MethodSection from "@/components/MethodSection";
import CTASection from "@/components/CTASection";

export default function About() {
  useSEO("about");
  const { tr } = useLang();
  const a = tr("about");
  const location = useLocation();
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    if (location.pathname === "/advisory") {
      const el = document.getElementById("advisory");
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.pathname]);

  return (
    <>
      {/* Header strip */}
      <section className="relative overflow-hidden bg-navy px-6 pb-20 pt-36 noise lg:px-10 lg:pb-28 lg:pt-44">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              <span className="h-px w-10 bg-gold/70" /> {a.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="about-title">
              {a.title?.[0]} <em className="text-gold-bright">{a.title?.[1]}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/65 sm:text-lg">{a.sub}</p>
          </Reveal>
        </div>
      </section>

      {/* Storia */}
      <section data-testid="story-section" className="bg-ivory py-20 text-ink lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 lg:px-10">
          <div className="md:col-span-4">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">
                <span className="h-px w-10 bg-gold" /> {a.storyEyebrow}
              </p>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight lg:text-4xl">{a.storyTitle}</h2>
            </Reveal>
          </div>
          <div className="space-y-6 md:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-ink/85">{a.storyP1}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-lg leading-relaxed text-ink-muted">{a.storyP2}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Advisory */}
      <section id="advisory" data-testid="advisory-section" className="relative overflow-hidden bg-navy py-20 noise lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-10 bg-gold/70" /> {a.advisoryEyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                {a.advisoryTitle?.split(" ").slice(0, -2).join(" ")}{" "}
                <em className="text-gold-bright">{a.advisoryTitle?.split(" ").slice(-2).join(" ")}</em>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base leading-relaxed text-ivory/65 sm:text-lg">{a.advisoryText}</p>
            </Reveal>
          </div>
          <div data-testid="advisory-services">
            {(a.services || []).map((s, i) => {
              const open = openIdx === i;
              return (
                <Reveal key={s.t} delay={i * 0.07}>
                  <div className={`border-t border-gold/15 transition-colors last:border-b ${open ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}>
                    <button
                      onClick={() => setOpenIdx(open ? null : i)}
                      data-testid={`advisory-service-${i + 1}`}
                      aria-expanded={open}
                      className="grid w-full gap-4 px-2 py-8 text-left md:grid-cols-[4rem_1fr_2fr_auto] md:items-center md:gap-10"
                    >
                      <span className="font-mono text-sm text-gold/80">0{i + 1}</span>
                      <h3 className={`font-serif text-2xl transition-colors ${open ? "text-gold-bright" : "text-ivory"}`}>{s.t}</h3>
                      <p className="hidden text-sm leading-relaxed text-ivory/60 md:block">{s.d}</p>
                      <motion.span
                        animate={{ rotate: open ? 45 : 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className={`flex h-10 w-10 items-center justify-center justify-self-end border transition-colors ${open ? "border-gold bg-gold text-navy" : "border-gold/30 text-gold-bright"}`}
                      >
                        <Plus className="h-4 w-4" />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                          data-testid={`advisory-detail-${i + 1}`}
                        >
                          <div className="grid gap-8 px-2 pb-9 md:grid-cols-[4rem_1fr_2fr] md:gap-10">
                            <span className="hidden md:block" />
                            <p className="self-start text-sm italic leading-relaxed text-gold-bright/85">{s.ideal}</p>
                            <div>
                              <ul className="space-y-2.5" data-testid={`advisory-bullets-${i + 1}`}>
                                {s.bullets.map((b) => (
                                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-ivory/70 md:text-base">
                                    <span className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-gold" /> {b}
                                  </li>
                                ))}
                              </ul>
                              <Link
                                to="/contact"
                                data-testid={`advisory-service-cta-${i + 1}`}
                                className="group mt-7 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-bright"
                              >
                                {a.serviceCta}
                                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <MethodSection />

      {/* Valori */}
      <section data-testid="values-section" className="bg-navy-light py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-10 bg-gold/70" /> {a.valuesEyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl tracking-tight text-ivory sm:text-4xl">
                {a.valuesTitle?.[0]} <em className="text-gold-bright">{a.valuesTitle?.[1]}</em>
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="values-grid">
            {(a.values || []).map((v, i) => (
              <Reveal key={v.t} delay={i * 0.09} className="h-full">
                <div className="h-full border-l-2 border-gold/50 pl-6">
                  <h3 className="font-serif text-2xl text-ivory">{v.t}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-ivory/55">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 flex justify-center">
              <Link
                to="/contact"
                data-testid="about-cta"
                className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-bright"
              >
                {a.ctaButton}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
